/**
 * useConvoyOfferDetail — fetches a Convoy `OfferPage` once per (load) and shares the
 * result with ALL consumers (ConvoyDetailSection in col 1, TripScheduleSection in col 2).
 *
 * Lifted to a hook + module-level cache so the network call is deduped — when the user
 * expands a Convoy load, two columns subscribe to the same offerId and they both get
 * the same in-flight Promise. Cache TTL is 5 minutes; OfferPage is mostly-static
 * (broker, address, accessorials don't change), and re-expanding the same load within
 * that window is the typical pattern.
 *
 * The cache is a bounded LRU (capacity 200) — without a cap a power user browsing
 * 300+ loads in a session would accumulate one Map entry per unique offer, with each
 * entry pinning a resolved Promise + ConvoyOfferDetail until page reload. 200 matches
 * the server-side `ralCache` in convoy-search.service.ts and covers typical browse
 * patterns; the oldest entry is evicted when a 201st distinct load is opened.
 */

import { CanonicalLoad } from '@numeo/types-core'
import { useEffect, useState } from 'react'
import { fetchConvoyOfferDetail, ConvoyOfferDetail } from '../api/convoy.api'

interface CacheEntry {
    promise: Promise<ConvoyOfferDetail | null>
    expiresAt: number
}

const CACHE_TTL_MS = 5 * 60 * 1000
const CACHE_MAX_SIZE = 200

/**
 * Tiny LRU built on `Map` — JS Maps preserve insertion order, so deleting + re-setting
 * an entry moves it to the "most recently used" tail; the head is the eviction target.
 *
 * We don't pull in `tiny-lru` (used server-side) here because:
 *   1. The frontend doesn't already have it as a transitive dep, and adding a 4-line
 *      utility is cheaper than another bundle entry.
 *   2. We need TTL-aware eviction that misses entries even when they're still under
 *      capacity — Map-based LRU lets us check expiry in `get()` cheaply.
 */
const detailCache = new Map<string, CacheEntry>()

function evictExpired(now: number): void {
    // Sweep up to N expired entries per call. Bounded work so a single hot-path lookup
    // never blocks on cleaning up a giant stale cache; the rest gets cleaned next time.
    let swept = 0
    for (const [key, entry] of detailCache) {
        if (entry.expiresAt <= now) {
            detailCache.delete(key)
            swept++
            if (swept >= 32) break
        }
    }
}

function getCachedDetail(offerId: string): Promise<ConvoyOfferDetail | null> {
    const now = Date.now()
    const cached = detailCache.get(offerId)
    if (cached) {
        if (cached.expiresAt > now) {
            // Hit — move to MRU end so it survives the next eviction wave.
            detailCache.delete(offerId)
            detailCache.set(offerId, cached)
            return cached.promise
        }
        // Expired — drop and fall through to refetch.
        detailCache.delete(offerId)
    }

    // Cheap opportunistic sweep so expired entries don't sit forever just because
    // their offerIds aren't being re-queried.
    if (detailCache.size >= CACHE_MAX_SIZE / 2) evictExpired(now)

    // If still over capacity after the sweep, evict the LRU entry (Map iterator order
    // gives us the oldest key first).
    if (detailCache.size >= CACHE_MAX_SIZE) {
        const oldestKey = detailCache.keys().next().value
        if (oldestKey) detailCache.delete(oldestKey)
    }

    const promise = fetchConvoyOfferDetail(offerId)
    detailCache.set(offerId, { promise, expiresAt: now + CACHE_TTL_MS })
    return promise
}

/** Return the Convoy offer-id from `externalId`. The backend strips the "CONVOY|" prefix
 * via `stripExternalIdPrefix` before sending the response, so `externalId` already is the
 * raw S-prefixed offer token by the time it reaches the frontend. */
function getConvoyOfferId(load: CanonicalLoad): string | null {
    if (load.sourceType !== 'CONVOY' || !load.externalId) return null
    return load.externalId
}

export interface UseConvoyOfferDetailResult {
    detail: ConvoyOfferDetail | null
    loading: boolean
    error: string | null
}

export function useConvoyOfferDetail(load: CanonicalLoad): UseConvoyOfferDetailResult {
    const offerId = getConvoyOfferId(load)
    const [detail, setDetail] = useState<ConvoyOfferDetail | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!offerId) {
            setDetail(null)
            setLoading(false)
            setError(null)
            return
        }
        let cancelled = false
        setLoading(true)
        setError(null)
        getCachedDetail(offerId)
            .then((data) => {
                if (!cancelled) setDetail(data)
            })
            .catch((err) => {
                if (!cancelled) {
                    setError((err as Error).message || 'Failed to load Convoy details')
                    // On failure, drop the cached entry so a retry can re-fetch.
                    detailCache.delete(offerId)
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [offerId])

    return { detail, loading, error }
}
